import { useQuery, gql, useMutation } from '@apollo/client'
import React, { useState } from 'react'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import { updateWithdrawReqStatus, withdrawRequestQuery } from '../apollo'
import Header from '../components/Headers/Header'
import {
  Alert,
  Box,
  Container,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { wideTableStyles } from '../utils/tableCustomStyles'
import StatusBadge from '../components/StatusBadge'
import useGlobalStyles from '../utils/globalStyles'
import SearchBar from '../components/TableHeader/SearchBar'
import TableHeader from '../components/TableHeader'
import { withTranslation, useTranslation } from 'react-i18next'

function WithdrawRequest() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const { loading, error, data } = useQuery(
    gql`
      ${withdrawRequestQuery}
    `
  )
  const [statusError, setStatusError] = useState('')
  // The resolver reports refusals (e.g. wallet too low) as success: false
  // rather than throwing, so surface its message here.
  const [updateStatus] = useMutation(
    gql`
      ${updateWithdrawReqStatus}
    `,
    {
      onCompleted: data => {
        const result = data && data.updateWithdrawReqStatus
        setStatusError(result && !result.success ? result.message : '')
      },
      onError: err => setStatusError(err.message)
    }
  )

  const handleSort = (column, sortDirection) =>
    console.log(column.selector, sortDirection)

  // Rows carry either a rider or an influencer, never both
  const requesterName = row =>
    row.requesterName ||
    (row.requesterType === 'INFLUENCER'
      ? row.influencer?.name
      : row.rider?.name) ||
    ''

  // Where the money actually goes. Riders keep a single account number;
  // influencers have full UK details captured by an admin.
  const payTo = row => {
    if (row.requesterType === 'INFLUENCER') {
      const bank = row.influencer?.bankDetails
      if (!bank || !bank.accountNumber) return null
      return {
        holder: bank.accountHolderName,
        bankName: bank.bankName,
        accountNumber: bank.accountNumber,
        sortCode: bank.sortCode
      }
    }
    const bank = row.rider?.bankDetails
    // Riders predating the bankDetails field only have a flat account number
    const accountNumber = bank?.accountNumber || row.rider?.accountNumber
    if (!accountNumber) return null
    return {
      holder: bank?.accountHolderName,
      bankName: bank?.bankName,
      accountNumber,
      sortCode: bank?.sortCode
    }
  }

  // Labelled so an admin can read off each field without guessing
  const bankLine = (label, value) =>
    value ? (
      <div>
        <span style={{ color: 'rgba(0,0,0,0.6)' }}>{label}: </span>
        <span>{value}</span>
      </div>
    ) : null

  const bankCell = row => {
    const details = payTo(row)
    if (!details) {
      return (
        <Typography style={{ color: '#b00020', fontSize: 12 }}>
          {t('NoBankDetails')}
        </Typography>
      )
    }
    return (
      <div style={{ fontSize: 12, lineHeight: 1.5, padding: '6px 0' }}>
        {bankLine(t('BankName'), details.bankName)}
        {bankLine(t('SortCode'), details.sortCode)}
        {bankLine(t('AccountNumber'), details.accountNumber)}
        {bankLine(t('AccountHolderName'), details.holder)}
      </div>
    )
  }

  const columns = [
    {
      name: t('RequestID'),
      selector: 'requestId',
      minWidth: '150px'
    },
    {
      name: t('RequestedBy'),
      sortable: true,
      selector: 'requesterName',
      minWidth: '140px',
      cell: row => <>{requesterName(row)}</>
    },
    {
      name: t('RequesterType'),
      sortable: true,
      selector: 'requesterType',
      minWidth: '120px',
      cell: row => (
        <>{t(row.requesterType === 'INFLUENCER' ? 'Influencer' : 'Rider')}</>
      )
    },

    {
      name: t('Amount'),
      selector: 'requestAmount',
      minWidth: '110px',
      right: true
    },
    {
      name: t('PayTo'),
      cell: row => <>{bankCell(row)}</>,
      minWidth: '200px'
    },
    {
      name: t('Date'),
      selector: 'requestTime',
      minWidth: '140px',
      cell: row => <>{new Date(row.requestTime).toDateString()}</>
    },
    {
      // A transferred payout is settled, so it reads as a plain badge; anything
      // else shows the badge inside the Select that can still change it.
      name: t('Status'),
      selector: 'status',
      minWidth: '190px',
      cell: row =>
        row.status === 'TRANSFERRED' ? (
          <StatusBadge status={row.status} />
        ) : (
          updateRequestStatus(row)
        )
    }
  ]

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.getAllWithdrawRequests.data
      : data &&
        data.getAllWithdrawRequests.data.filter(request => {
          return (
            request.requestId.toLowerCase().search(regex) > -1 ||
            requesterName(request).toLowerCase().search(regex) > -1
          )
        })

  // One control per row: the closed Select shows the current status as its
  // badge, and opening it offers the states to move to. Each option carries its
  // status as the value, so the selection and the mutation stay in step.
  const updateRequestStatus = row => {
    return (
      <Select
        id="input-status"
        name="input-status"
        inputProps={{ 'aria-label': t('Status') }}
        value={row.status}
        onChange={event => {
          const nextStatus = event.target.value
          if (nextStatus !== row.status) {
            updateStatus({ variables: { id: row._id, status: nextStatus } })
          }
        }}
        renderValue={status => <StatusBadge status={status} />}
        className={globalClasses.selectInput}
        sx={{
          height: 34,
          fontSize: 13,
          minWidth: 160,
          '& .MuiSelect-select': { display: 'flex', alignItems: 'center' }
        }}>
        <MenuItem value="REQUESTED">{t('REQUESTED')}</MenuItem>
        <MenuItem value="TRANSFERRED">{t('TRANSFERRED')}</MenuItem>
        <MenuItem value="CANCELLED">{t('CANCELLEDStatus')}</MenuItem>
      </Select>
    )
  }
  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header />
      <Container className={globalClasses.flex} fluid>
        {error ? <span> `Error! ${error.message}`</span> : null}
        {statusError ? (
          <Alert
            severity="error"
            onClose={() => setStatusError('')}
            sx={{ mb: 2 }}>
            {statusError}
          </Alert>
        ) : null}
        {loading ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                placeh
                // onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('WithdrawRequests')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loading}
            progressComponent={<CustomLoader />}
            onSort={handleSort}
            selectableRows
            customStyles={wideTableStyles(1180)}
          />
        )}
      </Container>
    </>
  )
}
export default withTranslation()(WithdrawRequest)
